interface UsageCheckResponse {
  allowed: boolean;
  hasLifetimeAccess: boolean;
  usage?: {
    games: { used: number; limit: number };
    analyses: { used: number; limit: number };
  };
  remaining?: number;
  limit?: number;
}

interface UsageIncrementResponse {
  success: boolean;
}

// Microservices URLs
const USAGE_URL = import.meta.env.VITE_USAGE_URL;
const PAYMENT_URL = import.meta.env.VITE_PAYMENT_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

export const checkUsage = async (
  userId: string,
  action: 'game' | 'analysis'
): Promise<UsageCheckResponse> => {
  const response = await fetch(`${USAGE_URL}/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ userId, action }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check usage');
  }

  return response.json();
};

export const incrementUsage = async (
  userId: string,
  action: 'game' | 'analysis'
): Promise<UsageIncrementResponse> => {
  const response = await fetch(`${USAGE_URL}/increment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ userId, action }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to increment usage');
  }

  return response.json();
};

export const createCheckoutSession = async (
  userId: string
): Promise<{ url: string }> => {
  const response = await fetch(`${PAYMENT_URL}/create-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  return response.json();
};

// Helper function to get current cached limits from store
// This is more efficient than making API calls every time
export const getCachedLimits = () => {
  // This function can be used by components that need access to limits
  // but don't want to import the full store
  return {
    freeGamesLimit: 1, // This would be replaced by actual store access in a real implementation
    freeAnalysisLimit: 1, // This would be replaced by actual store access in a real implementation
  };
};
